import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CaptionItem from '../atom/CaptionItem';
import SelectButton from '../atom/SelectButton';
import { AVAILABLE_CAPTIONS, REFERENCE_ID_LABEL } from '../constants/TranslationConstants';
import { CaptionData } from '../services/captionApiService';

interface CaptionListProps {
  referenceId: string;
  onSelect: (selectedCaption: CaptionData) => void;
  captionsData: CaptionData[];
}

const CaptionList = ({ referenceId, onSelect, captionsData }: CaptionListProps) => {
  const [selectedCaptionId, setSelectedCaptionId] = useState('');

  // Set the first caption as selected when captionsData changes
  React.useEffect(() => {
    if (captionsData.length > 0) {
      setSelectedCaptionId(captionsData[0].id);
    }
  }, [captionsData]);

  const handleSelect = () => {
    const selectedCaption = captionsData.find(caption => caption.id === selectedCaptionId);
    if (selectedCaption) {
      onSelect(selectedCaption);
    }
  };

  return (
    <Box>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          fontWeight: 'medium',
          mb: 2,
          color: 'text.primary',
          fontSize: { xs: '1rem', md: '1.1rem' }
        }}
      >
        {AVAILABLE_CAPTIONS}
      </Typography>

      <Box
        sx={{
          mb: 2,
          padding: '12px',
          backgroundColor: '#f0f8ff',
          border: '1px solid #006684',
          borderRadius: '6px',
          borderLeft: '4px solid #006684'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            fontWeight: 'medium',
            color: '#006684',
            marginBottom: '4px'
          }}
        >
          {REFERENCE_ID_LABEL}:
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
            fontFamily: 'monospace'
          }}
        >
          {referenceId}
        </Typography>
      </Box>

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          p: 2,
          mb: 3,
          backgroundColor: 'white',
          maxHeight: '180px',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        {captionsData.map((caption) => (
          <CaptionItem
            key={caption.id}
            id={caption.id}
            fileName={caption.fileName}
            language={caption.language}
            selected={selectedCaptionId === caption.id}
            onSelect={setSelectedCaptionId}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SelectButton
          onSelect={handleSelect}
          disabled={!selectedCaptionId}
        />
      </Box>
    </Box>
  );
};

export default CaptionList;